var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, InteractionManager, Platform, ScrollView, View, } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import styled from 'styled-components/native';
import { featureFlagAtom } from '@/atoms/Auth';
import { currentLocationAtom } from '@/atoms/Location';
import V2TabBar from './components/V2TabBar';
import { ScreenLayout } from '@/components/ScreenLayout';
import { color } from '@/constant/color';
import { AiSummarySourceTabDto, ElevatorCorrectionTargetDto, FloorMovingMethodTypeDto, PlaceDoorDirectionTypeDto, ReportTargetTypeDto, UpvoteTargetTypeDto, } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import useNavigateWithLocationCheck from '@/hooks/useNavigateWithLocationCheck';
import usePost from '@/hooks/usePost';
import { useToggleAccessibilityInfoRequest } from '@/hooks/useToggleAccessibilityInfoRequest';
import { useToggleFavoritePlace } from '@/hooks/useToggleFavoritePlace';
import { useUpvoteToggle } from '@/hooks/useUpvoteToggle';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { useLogger } from '@/logging/useLogger';
import ShareUtils from '@/utils/ShareUtils';
import { useCheckAuth } from '@/utils/checkAuth';
import { showAppInstallPrompt } from '@/utils/appInstallPrompt';
import { distanceInMeter } from '@/utils/DistanceUtils';
import ToastUtils from '@/utils/ToastUtils';
import { useIsFocused } from '@react-navigation/native';
import { useAtomValue } from 'jotai';
import V2AppBar from './components/V2AppBar';
import V2SummarySection from './components/V2SummarySection';
import BuildingRegistrationBottomSheet from './modals/BuildingRegistrationBottomSheet';
import { visibleAtom } from '../SearchScreen/atoms/quest';
import QuestCompletionModal from '../SearchScreen/components/QuestCompletionModal';
import NavigationAppsBottomSheet from '../PlaceDetailScreen/modals/NavigationAppsBottomSheet';
import PlaceDetailNegativeFeedbackBottomSheet from '../PlaceDetailScreen/modals/PlaceDetailNegativeFeedbackBottomSheet';
import RequireBuildingAccessibilityBottomSheet from '../PlaceDetailScreen/modals/RequireBuildingAccessibilityBottomSheet';
import { PlaceDetailFeedbackSection } from '../PlaceDetailScreen/sections/PlaceDetailFeedbackSection';
import { getAccessibilitySections } from './components/PlaceInfo.utils';
import V2HomeTab from './tabs/V2HomeTab';
import V2AccessibilityTab, { getAccessibilityChips, } from './tabs/V2AccessibilityTab';
import V2ChipBar from './components/V2ChipBar';
import V2ReviewTab from './tabs/V2ReviewTab';
import V2RestroomTab from './tabs/V2RestroomTab';
import V2ConquerorTab from './tabs/V2ConquerorTab';
import V2BottomBar from './components/V2BottomBar';
import V2ThumbnailRow from './components/V2ThumbnailRow';
import AiSummarySection from './components/AiSummarySection';
import PlaceDetailRegistrationSheet from './components/PlaceDetailRegistrationSheet';
const TAB_ITEMS = [
    { value: 'home', label: '홈' },
    { value: 'accessibility', label: '접근성' },
    { value: 'review', label: '리뷰' },
    { value: 'restroom', label: '화장실' },
    { value: 'conqueror', label: '정복자' },
];
export default function PlaceDetailV2Screen({ route, navigation, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const { event, placeInfo, autoOpenImageViewer, autoOpenImageIndex = 0, specialAccessibility: navSpecialAccessibility, } = route.params;
    const checkAuth = useCheckAuth();
    const { api } = useAppComponents();
    const { navigateWithLocationCheck, LocationConfirmModal } = useNavigateWithLocationCheck();
    const toggleFavorite = useToggleFavoritePlace();
    const toggleRequest = useToggleAccessibilityInfoRequest();
    const queryClient = useQueryClient();
    const featureFlags = useAtomValue(featureFlagAtom);
    const isCrew = (_a = featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.hasBeenCrew) !== null && _a !== void 0 ? _a : false;
    const reportAccessibilityMutation = usePost(['PlaceDetailV2', 'ReportAccessibility'], (params) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const result = yield api.reportAccessibilityPost(params);
        const isCrewClosure = isCrew && params.reason === 'CLOSED';
        if (isCrewClosure) {
            ToastUtils.show('폐업 처리가 완료되었습니다.');
            queryClient.invalidateQueries({ queryKey: ['searchPlaces'] });
            queryClient.invalidateQueries({ queryKey: ['PlaceDetailV2'] });
            // '정복 안 된 곳만 보기' 목록 캐시에서 폐업된 장소를 즉시 제거
            // (invalidate 대신 setQueriesData로 직접 필터 — 재요청 없이 UX 즉시 반영)
            queryClient.setQueriesData({ queryKey: ['SearchUnconqueredPlaces'] }, old => old === null || old === void 0 ? void 0 : old.filter(item => item.place.id !== params.placeId));
            navigation.goBack();
        }
        else {
            const isAutoResolved = ((_a = result.data) === null || _a === void 0 ? void 0 : _a.isAutoResolved) === true;
            ToastUtils.show(isAutoResolved ? '신고가 바로 반영되었어요!' : '신고가 접수되었어요.');
        }
    }));
    const isFocused = useIsFocused();
    const questModalVisible = useAtomValue(visibleAtom);
    const [pendingBottomSheet, setPendingBottomSheet] = useState(null);
    const [currentTab, setCurrentTab] = useState('home');
    const [showRegistrationSheet, setShowRegistrationSheet] = useState(false);
    const [scrollViewHeight, setScrollViewHeight] = useState(0);
    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    const openBottomSheet = useCallback((which) => {
        if (which === 'requireBuilding') {
            if (event &&
                (event === 'registration-force' || event === 'registration-suggest')) {
                setShowBuildingRegistrationBottomSheet(true);
            }
            else {
                setShowRequireBuildingAccessibilityBottomSheet(true);
            }
        }
    }, [event]);
    const placeId = 'placeId' in placeInfo ? placeInfo.placeId : placeInfo.place.id;
    const buildingId = 'building' in placeInfo ? placeInfo.building.id : undefined;
    const logParams = {
        place_id: placeId,
        building_id: buildingId,
        current_tab: currentTab,
    };
    const logger = useLogger(logParams);
    const loggerRef = useRef(logger);
    loggerRef.current = logger;
    const [showRequireBuildingAccessibilityBottomSheet, setShowRequireBuildingAccessibilityBottomSheet,] = useState(false);
    const [showBuildingRegistrationBottomSheet, setShowBuildingRegistrationBottomSheet,] = useState(false);
    const [showNavigationBottomSheet, setShowNavigationBottomSheet] = useState(false);
    const { data, isError: isPlaceError } = useQuery({
        initialData: {
            place: 'place' in placeInfo ? placeInfo.place : undefined,
            building: 'building' in placeInfo ? placeInfo.building : undefined,
            isAccessibilityRegistrable: 'isAccessibilityRegistrable' in placeInfo
                ? placeInfo.isAccessibilityRegistrable
                : undefined,
            accessibilityScore: 'accessibilityScore' in placeInfo
                ? placeInfo.accessibilityScore
                : undefined,
            kakaoPlaceId: undefined,
            specialAccessibility: navSpecialAccessibility,
            placeListNameChips: undefined,
        },
        queryKey: ['PlaceDetailV2', placeId],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ queryKey }) {
            var _b, _c;
            const result = yield api.getPlaceWithBuildingPost({ placeId: queryKey[1] });
            const kakaoVendor = (_b = result.data.vendorPlaceIds) === null || _b === void 0 ? void 0 : _b.find(v => v.vendorType === 'KAKAO');
            return {
                place: result.data.place,
                building: result.data.building,
                isAccessibilityRegistrable: result.data.isAccessibilityRegistrable,
                accessibilityScore: (_c = result.data.accessibilityInfo) === null || _c === void 0 ? void 0 : _c.accessibilityScore,
                kakaoPlaceId: kakaoVendor === null || kakaoVendor === void 0 ? void 0 : kakaoVendor.vendorPlaceId,
                specialAccessibility: result.data.specialAccessibility,
                placeListNameChips: result.data.placeListNameChips,
            };
        }),
    });
    const place = data === null || data === void 0 ? void 0 : data.place;
    const building = data === null || data === void 0 ? void 0 : data.building;
    const kakaoPlaceId = data === null || data === void 0 ? void 0 : data.kakaoPlaceId;
    const currentLocation = useAtomValue(currentLocationAtom);
    const distanceToPlace = useMemo(() => {
        if (!currentLocation || !(place === null || place === void 0 ? void 0 : place.location)) {
            return undefined;
        }
        return distanceInMeter(currentLocation, {
            latitude: place.location.lat,
            longitude: place.location.lng,
        });
    }, [currentLocation, place === null || place === void 0 ? void 0 : place.location]);
    const { data: accessibilityPost, isLoading, isFetching, } = useQuery({
        queryKey: ['PlaceDetailV2', placeId, 'Accessibility'],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ queryKey }) { return (yield api.getAccessibilityV2Post({ placeId: queryKey[1] })).data; }),
    });
    const { data: reviewPost } = useQuery({
        queryKey: ['PlaceDetailV2', placeId, UpvoteTargetTypeDto.PlaceReview],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ queryKey }) { return (yield api.listPlaceReviewsPost({ placeId: queryKey[1] })).data; }),
    });
    const { data: toiletPost } = useQuery({
        queryKey: ['PlaceDetailV2', placeId, UpvoteTargetTypeDto.ToiletReview],
        queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ queryKey }) { return (yield api.listToiletReviewsPost({ placeId: queryKey[1] })).data; }),
    });
    // Upvote state lifted from V2SummarySection & V2BottomBar so both stay in sync
    const placeUpvoteInfo = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeUpvoteInfo;
    const { isUpvoted, totalUpvoteCount, toggleUpvote } = useUpvoteToggle({
        initialIsUpvoted: (_b = placeUpvoteInfo === null || placeUpvoteInfo === void 0 ? void 0 : placeUpvoteInfo.isUpvoted) !== null && _b !== void 0 ? _b : false,
        initialTotalCount: placeUpvoteInfo === null || placeUpvoteInfo === void 0 ? void 0 : placeUpvoteInfo.totalUpvoteCount,
        targetId: placeId,
        targetType: 'PLACE',
        placeId: placeId,
    });
    const handleUpvote = useCallback(() => {
        checkAuth(() => toggleUpvote(), undefined, '앱에서 유용한 정보에 도움돼요를 눌러보세요');
    }, [checkAuth, toggleUpvote]);
    // API 에러 시 토스트 + goBack
    useEffect(() => {
        if (isPlaceError) {
            ToastUtils.show('장소 정보를 불러올 수 없습니다.');
            navigation.goBack();
        }
    }, [isPlaceError, navigation]);
    // 카드뷰 이미지 클릭 시 자동으로 이미지 뷰어 열기
    const hasAutoOpenedImageViewer = useRef(false);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (autoOpenImageViewer &&
            accessibilityPost &&
            !hasAutoOpenedImageViewer.current) {
            hasAutoOpenedImageViewer.current = true;
            const placeImages = ((_b = (_a = accessibilityPost.placeAccessibility) === null || _a === void 0 ? void 0 : _a.images) !== null && _b !== void 0 ? _b : []).map(img => ({ type: '장소 입구', url: img.imageUrl }));
            const buildingImages = ((_d = (_c = accessibilityPost.buildingAccessibility) === null || _c === void 0 ? void 0 : _c.entranceImages) !== null && _d !== void 0 ? _d : []).map(img => ({ type: '건물 입구', url: img.imageUrl }));
            const elevatorImages = ((_f = (_e = accessibilityPost.buildingAccessibility) === null || _e === void 0 ? void 0 : _e.elevatorImages) !== null && _f !== void 0 ? _f : []).map(img => ({ type: '엘리베이터', url: img.imageUrl }));
            const allImages = [...placeImages, ...buildingImages, ...elevatorImages];
            if (allImages.length > 0) {
                navigation.navigate('ImageZoomViewer', {
                    imageUrls: allImages.map(img => img.url),
                    index: autoOpenImageIndex,
                    types: allImages.map(img => img.type),
                });
            }
        }
    }, [autoOpenImageViewer, accessibilityPost, navigation]);
    // 네비게이션 블러 시 안전장치: 보류/열림 전부 닫기
    useEffect(() => {
        const unsub = navigation.addListener('blur', () => {
            setPendingBottomSheet(null);
            setShowRequireBuildingAccessibilityBottomSheet(false);
        });
        return unsub;
    }, [navigation]);
    useEffect(() => {
        var _a;
        // 등록된 정보 확인 후에 띄워주기
        if (!accessibilityPost)
            return;
        const isOutsideDoor = ((_a = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibility) === null || _a === void 0 ? void 0 : _a.doorDirectionType) ===
            PlaceDoorDirectionTypeDto.OutsideBuilding;
        // 어떤 바텀시트를 열지 결정
        let toOpen = null;
        if (event === 'submit-place' ||
            event === 'registration-suggest' ||
            event === 'registration-force') {
            if (!accessibilityPost.buildingAccessibility && !isOutsideDoor) {
                toOpen = 'requireBuilding';
            }
        }
        if (!toOpen)
            return;
        // 퀘스트 모달 떠 있으면 보류, 아니면 즉시 열기
        if (questModalVisible) {
            setPendingBottomSheet(toOpen);
        }
        else {
            openBottomSheet(toOpen);
        }
    }, [accessibilityPost, event, questModalVisible, openBottomSheet]);
    // 퀘스트 모달이 닫힌 뒤, 전환/애니메이션까지 끝난 다음 & 이 화면이 포커스일 때만 보류된 바텀시트 열기
    useEffect(() => {
        if (!questModalVisible && pendingBottomSheet) {
            const task = InteractionManager.runAfterInteractions(() => {
                if (isFocused) {
                    openBottomSheet(pendingBottomSheet);
                    setPendingBottomSheet(null);
                }
            });
            return () => task.cancel();
        }
    }, [questModalVisible, pendingBottomSheet, isFocused, openBottomSheet]);
    const closeModals = useCallback(() => {
        setShowRequireBuildingAccessibilityBottomSheet(false);
        setShowBuildingRegistrationBottomSheet(false);
        setPendingBottomSheet(null);
        navigation.setParams({ event: undefined });
    }, [navigation]);
    const onNavigateToOtherPage = useCallback(() => {
        navigation.setParams({ event: undefined });
        setPendingBottomSheet(null);
    }, [navigation]);
    const goToBuildingForm = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        closeModals();
        if (place && building) {
            yield navigateWithLocationCheck({
                targetLocation: building.location,
                address: building.address,
                type: 'building',
                onNavigate: () => {
                    navigation.navigate('BuildingFormV2', { place, building });
                },
            });
        }
    }), [building, closeModals, navigation, navigateWithLocationCheck, place]);
    const handleBuildingRegistrationConfirm = useCallback(() => {
        handleBuildingRegistrationCancel();
        if (place && building) {
            navigation.navigate('BuildingFormV2', { place, building });
        }
    }, [building, navigation, place]);
    const handleBuildingRegistrationCancel = useCallback(() => {
        setShowBuildingRegistrationBottomSheet(false);
        navigation.setParams({ event: undefined });
    }, [navigation]);
    const [reportTargetType, setReportTargetType] = useState(null);
    // Issue 1: Step 3(ReportCorrectionForm)에서 뒤로 올 때 bottom sheet를 복원하기 위해
    // navigate 전 reportTargetType을 ref에 저장하고, focus 이벤트에서 복원한다.
    const savedReportTargetTypeRef = useRef(null);
    // 신고 submit 성공 후 재오픈 시 내부 state(step, 선택값)를 초기화하기 위해
    // key를 증가시켜 강제 remount 시킨다. (back-cancel 복원 플로우에는 영향 없음)
    const [negativeFeedbackKey, setNegativeFeedbackKey] = useState(0);
    useEffect(() => {
        const unsubscribe = navigation.addListener('focus', () => {
            if (savedReportTargetTypeRef.current) {
                setReportTargetType(savedReportTargetTypeRef.current);
                savedReportTargetTypeRef.current = null;
            }
        });
        return unsubscribe;
    }, [navigation]);
    const showNegativeFeedbackBottomSheet = (type) => {
        checkAuth(() => {
            if (Platform.OS === 'web') {
                showAppInstallPrompt('이 기능은 앱에서만 이용할 수 있어요');
                return;
            }
            setReportTargetType(type);
        }, undefined, '신고 기능은 앱에서 이용할 수 있어요');
    };
    // 정보 등록 핸들러들
    const handlePlaceRegister = useCallback(() => {
        checkAuth(() => __awaiter(this, void 0, void 0, function* () {
            if (!place)
                return;
            yield navigateWithLocationCheck({
                targetLocation: place.location,
                placeName: place.name,
                address: place.address,
                type: 'place',
                onNavigate: () => {
                    navigation.navigate('PlaceFormV2', { place, building: building });
                },
            });
        }), undefined, '앱에서 접근성 정보를 등록해보세요');
    }, [building, checkAuth, navigation, navigateWithLocationCheck, place]);
    const handleBuildingRegister = useCallback(() => {
        checkAuth(() => __awaiter(this, void 0, void 0, function* () {
            if (!place || !building)
                return;
            yield navigateWithLocationCheck({
                targetLocation: building.location,
                address: building.address,
                type: 'building',
                onNavigate: () => {
                    navigation.navigate('BuildingFormV2', { place, building });
                },
            });
        }), undefined, '앱에서 접근성 정보를 등록해보세요');
    }, [building, checkAuth, navigation, navigateWithLocationCheck, place]);
    const handleReviewRegister = useCallback(() => {
        checkAuth(() => __awaiter(this, void 0, void 0, function* () {
            if (!place)
                return;
            yield navigateWithLocationCheck({
                targetLocation: place.location,
                placeName: place.name,
                address: place.address,
                type: 'place',
                onNavigate: () => {
                    navigation.navigate('ReviewForm/Place', {
                        placeId: place.id,
                    });
                },
            });
        }), undefined, '앱에서 방문 리뷰를 작성해보세요');
    }, [checkAuth, navigation, navigateWithLocationCheck, place]);
    const handleAddPlaceComment = useCallback(() => {
        checkAuth(() => {
            if (!place)
                return;
            if (Platform.OS === 'web') {
                showAppInstallPrompt('이 기능은 앱에서만 이용할 수 있어요');
                return;
            }
            navigation.navigate('AddComment', { type: 'place', placeId: place.id });
        }, undefined, '앱에서 코멘트를 남겨보세요');
    }, [checkAuth, navigation, place]);
    const handleAddBuildingComment = useCallback(() => {
        checkAuth(() => {
            if (!place || !building)
                return;
            if (Platform.OS === 'web') {
                showAppInstallPrompt('이 기능은 앱에서만 이용할 수 있어요');
                return;
            }
            navigation.navigate('AddComment', {
                type: 'building',
                placeId: place.id,
                buildingId: building.id,
            });
        }, undefined, '앱에서 코멘트를 남겨보세요');
    }, [building, checkAuth, navigation, place]);
    const handleToiletRegister = useCallback(() => {
        checkAuth(() => __awaiter(this, void 0, void 0, function* () {
            if (!place)
                return;
            yield navigateWithLocationCheck({
                targetLocation: place.location,
                placeName: place.name,
                address: place.address,
                type: 'place',
                onNavigate: () => {
                    navigation.navigate('ReviewForm/Toilet', {
                        placeId: place.id,
                    });
                },
            });
        }), undefined, '앱에서 화장실 정보를 등록해보세요');
    }, [checkAuth, navigation, navigateWithLocationCheck, place]);
    // 정보 등록 버튼: 모든 guard를 버튼 클릭 시점에 먼저 통과시키고, 다 만족돼야
    // 4지선다 바텀시트를 연다.
    //   1) auth guard — checkAuth: 비회원이면 로그인 유도(웹 팝업 / 네이티브 로그인)
    //   2) app guard  — 웹은 정보 등록(카메라)이 앱 전용이라 앱 설치 유도 팝업을 바로 띄우고 멈춘다
    //   3) 모든 guard 통과(현재는 네이티브만 해당) → 바텀시트
    const handlePressRegister = useCallback(() => {
        checkAuth(() => {
            if (Platform.OS === 'web') {
                showAppInstallPrompt('이 기능은 앱에서만 이용할 수 있어요');
                return;
            }
            setShowRegistrationSheet(true);
        }, undefined, '앱에서 접근성 정보를 등록해보세요');
    }, [checkAuth]);
    // Scroll-dependent AppBar title
    const [showAppBarTitle, setShowAppBarTitle] = useState(false);
    const nameBottomYRef = useRef(0);
    // Issue 4: Tab bar Y position for scroll preservation
    const tabBarYRef = useRef(0);
    // Issue 5: Current scroll position tracking
    const currentScrollYRef = useRef(0);
    // Chip bar state (accessibility tab)
    const scrollViewRef = useRef(null);
    const [activeChipIndex, setActiveChipIndex] = useState(0);
    const sectionLayoutsRef = useRef({});
    const loggedSectionsRef = useRef(new Set());
    const tabContentYRef = useRef(0);
    const isScrollingFromChipRef = useRef(false);
    const chips = useMemo(() => getAccessibilityChips(accessibilityPost), [accessibilityPost]);
    const showChipBar = currentTab === 'accessibility' && chips.length > 0;
    // 신고하기 step 2 옵션 필터링용 플래그 — PDP 홈탭에 실제로 렌더되는 정보에 1:1 대응.
    const negativeFeedbackFlags = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        // V2HomeTab과 동일한 fallback 패턴: 다중 배열 우선, 단일 legacy 필드 fallback.
        const placeAccessibilities = (_a = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibilities) !== null && _a !== void 0 ? _a : ((accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibility)
            ? [accessibilityPost.placeAccessibility]
            : []);
        const buildingAccessibilities = (_b = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.buildingAccessibilities) !== null && _b !== void 0 ? _b : ((accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.buildingAccessibility)
            ? [accessibilityPost.buildingAccessibility]
            : []);
        // V2HomeTab: PlaceEntranceSection/FloorMovementSection은 placeAccessibilities[0] 기준.
        const pa = placeAccessibilities[0];
        const hasAnyBa = buildingAccessibilities.length > 0;
        if (!pa) {
            return {
                hasPlaceAccessibility: false,
                hasBuildingAccessibility: false,
                hasPhoto: false,
                hasDoorType: false,
                elevatorTargets: [],
                placeAccessibilitySnapshot: undefined,
            };
        }
        // 참고: hasBuildingAccessibility 인자는 getAccessibilitySections 내부에서 현재 사용되지 않음.
        // 홈탭의 '건물 출입구' 섹션 포함 여부는 doorDir === InsideBuilding 등 구조적 조건으로만 결정된다.
        // 시그니처 상 required이므로 값을 그대로 전달한다.
        const homeSections = getAccessibilitySections({
            isStandalone: pa.isStandaloneBuilding === true,
            doorDir: (_c = pa.doorDirectionType) !== null && _c !== void 0 ? _c : undefined,
            isMultiFloor: ((_e = (_d = pa.floors) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0) > 1,
            hasV2Fields: pa.isStandaloneBuilding != null && pa.doorDirectionType != null,
            hasBuildingAccessibility: hasAnyBa,
        });
        // 건물 출입구 섹션이 PDP에 노출되고 + BA 실데이터가 있을 때만 (EmptySection일 땐 false)
        const hasBuildingAccessibility = homeSections.includes('건물 출입구') && hasAnyBa;
        // PDP 홈탭의 PhotoRow 3곳 중 하나라도 실제로 렌더되는가.
        // V2HomeTab의 BuildingEntranceSection은 모든 BA를 렌더하므로 사진 수도 전체 합산.
        const placePhotoCount = (_g = (_f = pa.images) === null || _f === void 0 ? void 0 : _f.length) !== null && _g !== void 0 ? _g : 0;
        const buildingPhotoCount = hasBuildingAccessibility
            ? buildingAccessibilities.reduce((sum, ba) => {
                var _a, _b, _c, _d;
                return sum +
                    ((_b = (_a = ba.entranceImages) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) +
                    ((_d = (_c = ba.elevatorImages) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0);
            }, 0)
            : 0;
        const floorMovingPhotoCount = homeSections.includes('층간 이동 정보')
            ? ((_k = (_j = (_h = pa.floorMovingElevatorAccessibility) === null || _h === void 0 ? void 0 : _h.imageUrls) === null || _j === void 0 ? void 0 : _j.length) !== null && _k !== void 0 ? _k : 0)
            : 0;
        const hasPhoto = placePhotoCount + buildingPhotoCount + floorMovingPhotoCount > 0;
        // 출입문 종류는 PA만 검사. BA 출입문 종류 신고는 건물 입구 신고 플로우에 포함.
        const hasDoorType = ((_m = (_l = pa.entranceDoorTypes) === null || _l === void 0 ? void 0 : _l.length) !== null && _m !== void 0 ? _m : 0) > 0;
        const elevatorTargets = [];
        // V2HomeTab이 모든 BA를 렌더하므로 어떤 BA든 엘리베이터가 있으면 옵션 노출.
        if (hasBuildingAccessibility &&
            buildingAccessibilities.some(ba => ba.hasElevator)) {
            elevatorTargets.push(ElevatorCorrectionTargetDto.Ba);
        }
        if (homeSections.includes('층간 이동 정보') &&
            ((_o = pa.floorMovingMethodTypes) === null || _o === void 0 ? void 0 : _o.includes(FloorMovingMethodTypeDto.PlaceElevator))) {
            elevatorTargets.push(ElevatorCorrectionTargetDto.Pa);
        }
        return {
            hasPlaceAccessibility: true,
            hasBuildingAccessibility,
            hasPhoto,
            hasDoorType,
            elevatorTargets,
            placeAccessibilitySnapshot: {
                floors: (_p = pa.floors) !== null && _p !== void 0 ? _p : undefined,
                isStandaloneBuilding: (_q = pa.isStandaloneBuilding) !== null && _q !== void 0 ? _q : undefined,
                entranceDoorTypes: (_r = pa.entranceDoorTypes) !== null && _r !== void 0 ? _r : undefined,
                accessibilityScore: (_s = data === null || data === void 0 ? void 0 : data.accessibilityScore) !== null && _s !== void 0 ? _s : undefined,
            },
        };
    }, [accessibilityPost, data === null || data === void 0 ? void 0 : data.accessibilityScore]);
    const handleSectionLayout = useCallback((chipName, y) => {
        sectionLayoutsRef.current[chipName] = y;
    }, []);
    const handleChipPress = useCallback((index) => {
        var _a, _b;
        setActiveChipIndex(index);
        isScrollingFromChipRef.current = true;
        const chipName = chips[index];
        const sectionY = (_a = sectionLayoutsRef.current[chipName]) !== null && _a !== void 0 ? _a : 0;
        const stickyHeight = stickyHeaderHeight || 91;
        const targetY = tabContentYRef.current + sectionY - stickyHeight;
        (_b = scrollViewRef.current) === null || _b === void 0 ? void 0 : _b.scrollTo({
            y: Math.max(0, targetY),
            animated: true,
        });
        setTimeout(() => {
            isScrollingFromChipRef.current = false;
        }, 500);
    }, [chips, stickyHeaderHeight]);
    const handleNameLayout = useCallback((e) => {
        const { y, height } = e.nativeEvent.layout;
        nameBottomYRef.current = y + height;
    }, []);
    const handleScroll = useCallback((e) => {
        var _a;
        const scrollY = e.nativeEvent.contentOffset.y;
        currentScrollYRef.current = scrollY;
        const shouldShow = scrollY > nameBottomYRef.current;
        setShowAppBarTitle(prev => (prev !== shouldShow ? shouldShow : prev));
        // Active chip tracking for accessibility tab (skip during programmatic scroll)
        if (showChipBar && chips.length > 0 && !isScrollingFromChipRef.current) {
            const stickyHeight = stickyHeaderHeight || 91;
            const adjustedY = scrollY + stickyHeight;
            let newIndex = 0;
            for (let i = chips.length - 1; i >= 0; i--) {
                const sectionAbsY = tabContentYRef.current + ((_a = sectionLayoutsRef.current[chips[i]]) !== null && _a !== void 0 ? _a : 0);
                if (adjustedY >= sectionAbsY) {
                    newIndex = i;
                    break;
                }
            }
            setActiveChipIndex(prev => (prev !== newIndex ? newIndex : prev));
        }
        // Section viewport detection for element_view (accessibility tab only)
        if (showChipBar) {
            const viewportHeight = e.nativeEvent.layoutMeasurement.height;
            Object.entries(sectionLayoutsRef.current).forEach(([sectionName, sectionY]) => {
                const absoluteY = tabContentYRef.current + sectionY;
                if (!loggedSectionsRef.current.has(sectionName) &&
                    absoluteY < scrollY + viewportHeight &&
                    absoluteY + 200 > scrollY) {
                    loggedSectionsRef.current.add(sectionName);
                    loggerRef.current.logElementView(`pdp_v2_section_${sectionName}`);
                }
            });
        }
    }, [showChipBar, chips, stickyHeaderHeight, placeId]);
    // Issue 5: Tab change with scroll adjustment
    const handleTabChange = useCallback((newTab) => {
        setCurrentTab(newTab);
        setActiveChipIndex(0);
        loggerRef.current.logElementView(`pdp_v2_${newTab}_tab`, {
            current_tab: newTab, // setCurrentTab 전이라 localParams의 값은 이전 탭
        });
        // 탭바가 sticky된 상태(스크롤이 요약 섹션 하단 이후)이면
        // 탭바 상단으로 스크롤 리셋 (chip bar 높이 차이 보정)
        if (currentScrollYRef.current > tabBarYRef.current) {
            requestAnimationFrame(() => {
                var _a;
                (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
                    y: tabBarYRef.current,
                    animated: false,
                });
            });
        }
    }, [placeId]);
    const bbucleRoadUrl = (_d = (_c = data === null || data === void 0 ? void 0 : data.specialAccessibility) === null || _c === void 0 ? void 0 : _c.bbucleRoadData) === null || _d === void 0 ? void 0 : _d.bbucleRoadUrl;
    // BbucleRoad: WebViewScreen으로 replace하여 코드 중복 제거.
    // WebViewScreen이 이미 SccContentFloatingBar, handleWebViewShouldStartLoad 등을 처리.
    const [didReplaceToBbucleRoad, setDidReplaceToBbucleRoad] = useState(false);
    useEffect(() => {
        if (bbucleRoadUrl && place && !didReplaceToBbucleRoad) {
            setDidReplaceToBbucleRoad(true);
            navigation.replace('Webview', {
                url: bbucleRoadUrl,
                fixedTitle: place.name,
                // web: bbucleRoadUrl(외부 컨텐츠)로 현재 탭을 그대로 이동. 새 탭이면 데스크톱에서
                // 팝업 차단 + 빈 Webview goBack → /home 으로 튄다.
                webLinkTarget: '_self',
            });
        }
    }, [bbucleRoadUrl, place, navigation, didReplaceToBbucleRoad]);
    if (isLoading || !place || !building) {
        return null;
    }
    const renderTabContent = () => {
        switch (currentTab) {
            case 'home':
                return (_jsx(V2HomeTab, { accessibility: accessibilityPost, place: place, building: building, reviews: reviewPost !== null && reviewPost !== void 0 ? reviewPost : [], toiletReviews: toiletPost !== null && toiletPost !== void 0 ? toiletPost : [], kakaoPlaceId: kakaoPlaceId, isAccessibilityInfoRequested: accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.isAccessibilityInfoRequested, isAccessibilityRegistrable: data === null || data === void 0 ? void 0 : data.isAccessibilityRegistrable, onRequestInfo: () => {
                        checkAuth(() => {
                            toggleRequest({
                                currentIsRequested: accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.isAccessibilityInfoRequested,
                                placeId: place.id,
                            });
                        }, undefined, '앱에서 접근성 정보를 요청해보세요');
                    }, onPressAccessibilityTab: () => setCurrentTab('accessibility'), onPressReviewTab: () => setCurrentTab('review'), onPressDirections: () => setShowNavigationBottomSheet(true), onPressPlaceRegister: handlePlaceRegister, onPressBuildingRegister: handleBuildingRegister, onPressReviewRegister: handleReviewRegister, onPressToiletRegister: handleToiletRegister }));
            case 'accessibility':
                return (_jsx(V2AccessibilityTab, { accessibility: accessibilityPost, place: place, building: building, isAccessibilityRegistrable: data === null || data === void 0 ? void 0 : data.isAccessibilityRegistrable, reviews: reviewPost !== null && reviewPost !== void 0 ? reviewPost : [], onRegister: handlePlaceRegister, onBuildingRegister: handleBuildingRegister, onAddPlaceComment: handleAddPlaceComment, onAddBuildingComment: handleAddBuildingComment, showNegativeFeedbackBottomSheet: showNegativeFeedbackBottomSheet, allowDuplicateRegistration: true, onSectionLayout: handleSectionLayout }));
            case 'review':
                return (_jsx(V2ReviewTab, { reviews: reviewPost !== null && reviewPost !== void 0 ? reviewPost : [], place: place, isAccessibilityRegistrable: data === null || data === void 0 ? void 0 : data.isAccessibilityRegistrable }));
            case 'restroom':
                return (_jsx(V2RestroomTab, { toiletReviews: toiletPost !== null && toiletPost !== void 0 ? toiletPost : [], placeId: place.id, placeName: place.name, placeLocation: place.location, placeAddress: place.address, isAccessibilityRegistrable: data === null || data === void 0 ? void 0 : data.isAccessibilityRegistrable }));
            case 'conqueror':
                return (_jsx(V2ConquerorTab, { accessibility: accessibilityPost, onPressRegister: handlePlaceRegister }));
            default:
                return null;
        }
    };
    const showFeedbackSection = accessibilityPost &&
        ((accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibility) ||
            (accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.buildingAccessibility) ||
            ((_f = (_e = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibilities) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0) > 0 ||
            ((_h = (_g = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.buildingAccessibilities) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 0) > 0) &&
        (data === null || data === void 0 ? void 0 : data.isAccessibilityRegistrable);
    return (_jsxs(LogParamsProvider, { params: logParams, children: [_jsxs(ScreenLayout, { isHeaderVisible: false, safeAreaEdges: ['top'], children: [_jsxs(GestureHandlerRootView, { style: { flex: 1 }, children: [_jsx(V2AppBar, { isFavorite: place.isFavorite, onToggleFavorite: () => checkAuth(() => toggleFavorite({
                                    currentIsFavorite: place.isFavorite,
                                    placeId: place.id,
                                }), undefined, '앱에서 마음에 드는 장소를 저장해보세요'), onShare: () => ShareUtils.sharePlace(place), placeName: place.name, showTitle: showAppBarTitle, onBack: () => navigation.goBack() }), _jsxs(ScrollView, { ref: scrollViewRef, style: { flex: 1 }, scrollEventThrottle: 16, onScroll: handleScroll, onLayout: e => setScrollViewHeight(e.nativeEvent.layout.height), stickyHeaderIndices: [1], children: [_jsxs(SummarySectionContainer, { children: [_jsx(V2SummarySection, { place: place, accessibilityScore: data === null || data === void 0 ? void 0 : data.accessibilityScore, accessibility: accessibilityPost, reviewCount: (reviewPost !== null && reviewPost !== void 0 ? reviewPost : []).length, placeListNameChips: (_j = data === null || data === void 0 ? void 0 : data.placeListNameChips) !== null && _j !== void 0 ? _j : undefined, onNameLayout: handleNameLayout }), _jsx(AiSummarySection, { placeId: placeId, aiSummary: accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.aiSummary, onPressSourceTab: sourceTab => {
                                                    handleTabChange(sourceTab === AiSummarySourceTabDto.Review
                                                        ? 'review'
                                                        : 'accessibility');
                                                    // 요약 섹션(상단)에서 눌렀을 땐 탭바가 아직 화면 아래에 있으므로,
                                                    // 탭 내용이 화면을 채우도록 탭바 상단으로 스크롤한다.
                                                    requestAnimationFrame(() => {
                                                        var _a;
                                                        return (_a = scrollViewRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
                                                            y: tabBarYRef.current,
                                                            animated: true,
                                                        });
                                                    });
                                                } }), _jsx(V2ThumbnailRow, { accessibility: accessibilityPost, reviews: reviewPost !== null && reviewPost !== void 0 ? reviewPost : [], toiletReviews: toiletPost !== null && toiletPost !== void 0 ? toiletPost : [] })] }), _jsxs(View, { onLayout: e => {
                                            tabBarYRef.current = e.nativeEvent.layout.y;
                                            setStickyHeaderHeight(e.nativeEvent.layout.height);
                                        }, children: [_jsx(V2TabBar, { items: TAB_ITEMS, current: currentTab, onChange: handleTabChange }), showChipBar && (_jsx(V2ChipBar, { chips: chips, activeIndex: activeChipIndex, onChipPress: handleChipPress }))] }), _jsxs(View, { onLayout: e => {
                                            tabContentYRef.current = e.nativeEvent.layout.y;
                                        }, style: {
                                            minHeight: scrollViewHeight > 0
                                                ? scrollViewHeight - (stickyHeaderHeight || 41) - 110
                                                : 0,
                                            backgroundColor: currentTab !== 'home' ? color.gray5 : color.white,
                                        }, children: [renderTabContent(), showFeedbackSection && currentTab === 'home' && (_jsx(PlaceDetailFeedbackSection, { placeId: placeId, accessibility: accessibilityPost }))] }), _jsx(BottomPadding, {})] })] }), _jsx(View, { style: { position: 'absolute', bottom: 0, left: 0, right: 0 }, children: _jsx(V2BottomBar, { accessibility: accessibilityPost, isUpvoted: isUpvoted, totalUpvoteCount: totalUpvoteCount, onPressUpvote: handleUpvote, onPressRegister: handlePressRegister, onPressWriteReview: handleReviewRegister, onPressSiren: () => showNegativeFeedbackBottomSheet(ReportTargetTypeDto.PlaceAccessibility) }) }), _jsx(RequireBuildingAccessibilityBottomSheet, { isVisible: !isFetching && showRequireBuildingAccessibilityBottomSheet, isFirstFloor: (_k = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibility) === null || _k === void 0 ? void 0 : _k.isFirstFloor, onPressConfirmButton: goToBuildingForm, onPressCloseButton: closeModals }), _jsx(QuestCompletionModal, { onMoveToQuestClearPage: onNavigateToOtherPage }), event &&
                        (event === 'registration-force' ||
                            event === 'registration-suggest') && (_jsx(BuildingRegistrationBottomSheet, { isVisible: showBuildingRegistrationBottomSheet, event: event, onPressConfirm: handleBuildingRegistrationConfirm, onPressCancel: handleBuildingRegistrationCancel })), _jsx(PlaceDetailRegistrationSheet, { isVisible: showRegistrationSheet, onClose: () => setShowRegistrationSheet(false), onPressPlaceRegister: handlePlaceRegister, onPressBuildingRegister: handleBuildingRegister, onPressReviewRegister: handleReviewRegister, onPressToiletRegister: handleToiletRegister, isAccessibilityRegistrable: data === null || data === void 0 ? void 0 : data.isAccessibilityRegistrable })] }), (place === null || place === void 0 ? void 0 : place.id) && (_jsx(PlaceDetailNegativeFeedbackBottomSheet, { isVisible: reportTargetType !== null, placeId: place.id, hasPlaceAccessibility: negativeFeedbackFlags.hasPlaceAccessibility, hasBuildingAccessibility: negativeFeedbackFlags.hasBuildingAccessibility, hasPhoto: negativeFeedbackFlags.hasPhoto, hasDoorType: negativeFeedbackFlags.hasDoorType, elevatorTargets: negativeFeedbackFlags.elevatorTargets, placeAccessibilitySnapshot: negativeFeedbackFlags.placeAccessibilitySnapshot, onPressCloseButton: () => {
                    setReportTargetType(null);
                }, onPressSubmitButton: (_placeId, reason, text) => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b;
                    if (!reportTargetType) {
                        return;
                    }
                    const targetType = reportTargetType;
                    setReportTargetType(null);
                    reportAccessibilityMutation.mutate({
                        placeId: _placeId,
                        placeAccessibilityId: (_a = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibility) === null || _a === void 0 ? void 0 : _a.id,
                        buildingAccessibilityId: (_b = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.buildingAccessibility) === null || _b === void 0 ? void 0 : _b.id,
                        reason,
                        targetType,
                        detail: text,
                    });
                }), onPressNavigateToCorrection: params => {
                    savedReportTargetTypeRef.current = reportTargetType;
                    setReportTargetType(null);
                    setTimeout(() => {
                        navigation.navigate('ReportCorrectionForm', {
                            placeId: params.placeId,
                            inaccurateCategory: params.inaccurateCategories[0],
                            elevatorTarget: params.elevatorTarget,
                            onSubmitSuccess: () => {
                                savedReportTargetTypeRef.current = null;
                                setNegativeFeedbackKey(k => k + 1);
                            },
                        });
                    }, 300);
                }, onPressSubmitClosed: params => {
                    if (!reportTargetType) {
                        return;
                    }
                    const submitClosed = () => {
                        var _a, _b;
                        const targetType = reportTargetType;
                        setReportTargetType(null);
                        reportAccessibilityMutation.mutate({
                            placeId: params.placeId,
                            placeAccessibilityId: (_a = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.placeAccessibility) === null || _a === void 0 ? void 0 : _a.id,
                            buildingAccessibilityId: (_b = accessibilityPost === null || accessibilityPost === void 0 ? void 0 : accessibilityPost.buildingAccessibility) === null || _b === void 0 ? void 0 : _b.id,
                            reason: 'CLOSED',
                            targetType,
                            correction: {
                                closedSubType: params.closedSubType,
                                noteText: params.detail,
                            },
                        });
                    };
                    if (isCrew) {
                        Alert.alert('폐업 신고', '크루가 신고하는 경우 즉시 폐업됩니다. 정말 폐업 신고하시겠어요?', [
                            { text: '취소', style: 'cancel' },
                            {
                                text: '폐업 신고',
                                style: 'destructive',
                                onPress: submitClosed,
                            },
                        ]);
                    }
                    else {
                        submitClosed();
                    }
                } }, negativeFeedbackKey)), (place === null || place === void 0 ? void 0 : place.location) && (_jsx(NavigationAppsBottomSheet, { isVisible: showNavigationBottomSheet, latitude: place.location.lat, longitude: place.location.lng, placeName: place.name, distanceMeters: distanceToPlace, onClose: () => setShowNavigationBottomSheet(false) })), LocationConfirmModal] }));
}
const SummarySectionContainer = styled.View `
  padding-top: 4px;
  padding-bottom: 20px;
  gap: 20px;
`;
const BottomPadding = styled.View `
  height: 110px;
  background-color: transparent;
`;
